import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useFormContext } from 'react-form';
import type { debounce } from 'lodash';
import { validateStep, type Steps } from '../helpers/validateFormFields';
import type { SelfSignUpForm } from '../types';
import type { ProductPlan } from '../productPlan';
import { useCaptureLead } from './useCaptureLead';

type SelfSignUpContextType = {
  validationStep: Steps;
  isSignUpComplete: boolean;
  isNextButtonDisabled: boolean;
  isSignUpStarted: boolean;
  isSignUpErrored: boolean;
  isStepNavigationBlocked: boolean;
  handleCaptureLead: ReturnType<typeof debounce>;
  handleReset: () => void;
  onSwitchPlan: (productPlan: ProductPlan) => void;
  setValidationStep: (validationStep: Steps) => void;
  setIsSignUpComplete: (isSignUpComplete: boolean) => void;
  setIsSignUpStarted: (isSignUpStarted: boolean) => void;
  setIsSignUpErrored: (isSignUpErrored: boolean) => void;
  setIsStepNavigationBlocked: (isStepNavigationBlocked: boolean) => void;
};

const SelfSignUpContext = createContext<SelfSignUpContextType>(null as unknown as SelfSignUpContextType);

type SelfSignUpProviderProps = {
  children: React.ReactNode;
  initialStep?: Steps;
  onSwitchPlan: (productPlan: ProductPlan, values: Partial<SelfSignUpForm>) => void;
};

export const SelfSignUpProvider = ({ children, initialStep = 'restaurant_info_step', onSwitchPlan }: SelfSignUpProviderProps) => {
  const [isSignUpComplete, setIsSignUpComplete] = useState(false);
  const [isSignUpStarted, setIsSignUpStarted] = useState(false);
  const [isSignUpErrored, setIsSignUpErrored] = useState(false);
  const [isStepNavigationBlocked, setIsStepNavigationBlocked] = useState(false);
  const [validationStep, setValidationStep] = useState<Steps>(initialStep);
  const form = useFormContext<SelfSignUpForm>();

  const handleReset = useCallback(() => {
    setIsSignUpComplete(false);
    setIsSignUpStarted(false);
    setIsSignUpErrored(false);
    setIsStepNavigationBlocked(false);
    setValidationStep('restaurant_info_step');
  }, []);

  const handleSwitchPlan = useCallback((productPlan: ProductPlan) => {
    onSwitchPlan(productPlan, form.values);
  }, [onSwitchPlan, form.values]);

  const isStepValid = useMemo(() => validateStep(validationStep, form.values, form.values.productPlan), [validationStep, form.values]);
  const isFormValid = form.meta.isValid;

  const isNextButtonDisabled = !isStepValid || !isFormValid || isStepNavigationBlocked;

  const handleCaptureLead = useCaptureLead(validationStep);

  const value = useMemo(() => ({
    handleCaptureLead,
    handleReset,
    isNextButtonDisabled,
    isSignUpComplete,
    isSignUpErrored,
    isSignUpStarted,
    isStepNavigationBlocked,
    onSwitchPlan: handleSwitchPlan,
    setIsSignUpComplete,
    setIsSignUpErrored,
    setIsSignUpStarted,
    setIsStepNavigationBlocked,
    setValidationStep,
    validationStep,
  }), [handleCaptureLead, handleReset, handleSwitchPlan, isNextButtonDisabled, isSignUpComplete, isSignUpErrored, isSignUpStarted, isStepNavigationBlocked, validationStep, setValidationStep, setIsSignUpComplete, setIsSignUpErrored, setIsSignUpStarted, setIsStepNavigationBlocked]);

  return (
    <SelfSignUpContext.Provider value={value}>
      {children}
    </SelfSignUpContext.Provider>
  );
};

export const useSelfSignUpContext = () => {
  const context = useContext(SelfSignUpContext);

  if (!context) {
    throw new Error('useSelfSignUpContext must be used within a SelfSignUpProvider');
  }

  return context;
};
